import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Stack, TextField, MenuItem, Button, Alert,
  CircularProgress, Paper, Divider, FormGroup, FormControlLabel, Checkbox, Popover,
} from "@mui/material";
import LanguageIcon from "@mui/icons-material/Language";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../api";
import { mediaUrl } from "../../utils/media";
import { FONT_MAP, loadGoogleFont, headerColor, readableText } from "../../utils/salonTheme";
import { THEME_PRESETS, presetName } from "../../utils/themePresets";
import ColorPicker from "../../components/common/ColorPicker";

const LANGS = [
  { code: "pl", label: "Polski" },
  { code: "uk", label: "Українська" },
  { code: "en", label: "English" },
];
const FONTS = ["Montserrat", "Inter", "Poppins", "Playfair Display", "Lora", "Roboto"];
const HEX = /^#[0-9a-fA-F]{6}$/;

// Рядок вибору кольору: свотч (клік → інлайн-пікер із перетягуванням) + hex-поле.
// Модульний рівень — щоб стан поповера не скидався під час live-оновлення теми.
function ColorField({ label, hint, val, set, disabled, t }) {
  const [anchor, setAnchor] = useState(null);
  return (
    <Stack direction="row" spacing={2} alignItems="center">
      <Box
        onClick={(e) => !disabled && setAnchor(e.currentTarget)}
        sx={{
          width: 48, height: 40, borderRadius: 1, flexShrink: 0,
          border: "1px solid", borderColor: "divider", bgcolor: val,
          cursor: disabled ? "default" : "pointer",
        }}
      />
      <Box sx={{ minWidth: 0, flexGrow: 1 }}>
        <Typography variant="body2" fontWeight={600} noWrap>{label}</Typography>
        {hint && <Typography variant="caption" color="text.secondary">{hint}</Typography>}
      </Box>
      <TextField
        value={val}
        onChange={(e) => set(e.target.value)}
        size="small"
        disabled={disabled}
        sx={{ width: 120 }}
        inputProps={{ maxLength: 7, style: { fontFamily: "monospace" } }}
      />
      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Box sx={{ p: 1.5 }}>
          <ColorPicker value={val} onChange={set} disabled={disabled} />
          <TextField
            value={val}
            onChange={(e) => set(e.target.value)}
            size="small"
            fullWidth
            sx={{ mt: 1.5 }}
            inputProps={{ maxLength: 7, style: { fontFamily: "monospace" } }}
          />
          <Button size="small" fullWidth variant="contained" sx={{ mt: 1 }} onClick={() => setAnchor(null)}>
            {t("common.done")}
          </Button>
        </Box>
      </Popover>
    </Stack>
  );
}

const DEF = {
  primary: "#6c1129",
  accent: "#b99a5b",
  header: "#6c1129",
  background: "#ffffff",
  surface: "#ffffff",
  text: "#1f2937",
};

export default function ThemeEditor() {
  const { t, i18n } = useTranslation();
  const { activeWorkspaceId, workspaceRole, activeWorkspace } = useAuth();
  const isOwner = workspaceRole === "owner";

  const [primary, setPrimary] = useState(DEF.primary);
  const [accent, setAccent] = useState(DEF.accent);
  const [header, setHeader] = useState(DEF.header);
  const [background, setBackground] = useState(DEF.background);
  const [surface, setSurface] = useState(DEF.surface);
  const [text, setText] = useState(DEF.text);
  const [font, setFont] = useState("Montserrat");
  const [logoUrl, setLogoUrl] = useState("");
  const [bgImageUrl, setBgImageUrl] = useState("");
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingBg, setUploadingBg] = useState(false);
  const [defaultLang, setDefaultLang] = useState("pl");
  const [enabledLangs, setEnabledLangs] = useState(["pl", "uk", "en"]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [ok, setOk] = useState("");

  const load = useCallback(async () => {
    if (!activeWorkspaceId) return;
    setLoading(true);
    setError("");
    try {
      const res = await api.getWorkspaceSettings(activeWorkspaceId);
      const th = res.settings?.theme || {};
      setPrimary(th.primary || DEF.primary);
      setAccent(th.accent || DEF.accent);
      setHeader(th.header || th.primary || DEF.header);
      setBackground(th.background || DEF.background);
      setSurface(th.surface || DEF.surface);
      setText(th.text || DEF.text);
      setFont(th.font || "Montserrat");
      setLogoUrl(th.logoUrl || "");
      setBgImageUrl(th.bgImageUrl || "");
      setDefaultLang(res.settings?.defaultLang || "pl");
      setEnabledLangs(res.settings?.enabledLangs || ["pl", "uk", "en"]);
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  }, [activeWorkspaceId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadGoogleFont(font); }, [font]);
  // Прелоад шрифтів усіх пресетів — щоб міні-превʼю галереї показувались у своєму шрифті.
  useEffect(() => { THEME_PRESETS.forEach((p) => loadGoogleFont(p.font)); }, []);

  const onLogoPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploadingLogo(true);
    setError("");
    try {
      const { url } = await api.uploadWorkspaceImage(activeWorkspaceId, file);
      setLogoUrl(url);
    } catch (er) {
      setError(er.message);
    }
    setUploadingLogo(false);
  };

  const onBgPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploadingBg(true);
    setError("");
    try {
      const { url } = await api.uploadWorkspaceImage(activeWorkspaceId, file);
      setBgImageUrl(url);
    } catch (er) {
      setError(er.message);
    }
    setUploadingBg(false);
  };

  const toggleLang = (code) => {
    setEnabledLangs((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    );
  };

  const resetColors = () => {
    setPrimary(DEF.primary); setAccent(DEF.accent); setHeader(DEF.header);
    setBackground(DEF.background); setSurface(DEF.surface); setText(DEF.text);
  };

  // Застосувати готовий дизайн-пресет (кольори + шрифт + фон). Далі можна донастроїти.
  const applyPreset = (p) => {
    const c = p.colors;
    setPrimary(c.primary); setAccent(c.accent); setHeader(c.header);
    setBackground(c.background); setSurface(c.surface); setText(c.text);
    setFont(p.font); setBgImageUrl(p.bgImageUrl || "");
  };

  const save = async () => {
    setError("");
    setOk("");
    const all = { primary, accent, header, background, surface, text };
    if (Object.values(all).some((c) => !HEX.test(c))) {
      setError(t("themeEditor.badColor"));
      return;
    }
    if (enabledLangs.length === 0) {
      setError(t("themeEditor.needLang"));
      return;
    }
    if (!enabledLangs.includes(defaultLang)) {
      setError(t("themeEditor.defaultMustBeEnabled"));
      return;
    }
    setSaving(true);
    try {
      await api.updateWorkspaceSettings(activeWorkspaceId, {
        theme: { ...all, font, logoUrl, bgImageUrl },
        defaultLang,
        enabledLangs,
      });
      setOk(t("themeEditor.saved"));
    } catch (e) {
      setError(e.message);
    }
    setSaving(false);
  };

  if (!activeWorkspaceId) {
    return (
      <Box sx={{ py: 4 }}>
        <Typography color="text.secondary">{t("themeEditor.noWorkspace")}</Typography>
      </Box>
    );
  }

  const ff = FONT_MAP[font] || `'${font}', sans-serif`;
  const themeObj = { primary, accent, header, background, surface, text, logoUrl };
  const headBg = headerColor(themeObj);
  const salonName = activeWorkspace?.name || t("themeEditor.previewSalon");

  return (
    <Box sx={{ py: 2, maxWidth: 1080 }}>
      <Typography variant="h5" sx={{ mb: 0.5 }}>{t("themeEditor.title")}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t("themeEditor.subtitle")}
      </Typography>

      {!isOwner && <Alert severity="info" sx={{ mb: 2 }}>{t("themeEditor.ownerOnly")}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>{error}</Alert>}
      {ok && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setOk("")}>{ok}</Alert>}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
        {/* --- Готові теми (пресети) --- */}
        <Paper sx={{ p: 2.5, mb: 3 }}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>{t("themeEditor.presetsTitle")}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: "block" }}>
            {t("themeEditor.presetsHint")}
          </Typography>
          <Box sx={{ display: "grid", gap: 1.5, gridTemplateColumns: { xs: "repeat(2,1fr)", sm: "repeat(3,1fr)", md: "repeat(4,1fr)" } }}>
            {THEME_PRESETS.map((p) => {
              const c = p.colors;
              const pff = FONT_MAP[p.font] || `'${p.font}', sans-serif`;
              return (
                <Box
                  key={p.id}
                  onClick={() => isOwner && applyPreset(p)}
                  role="button"
                  aria-label={presetName(p, i18n.language)}
                  sx={{
                    borderRadius: 2, overflow: "hidden", border: "1px solid", borderColor: "divider",
                    cursor: isOwner ? "pointer" : "default", transition: "0.15s",
                    "&:hover": isOwner ? { boxShadow: 4, borderColor: "primary.main", transform: "translateY(-2px)" } : {},
                  }}
                >
                  {/* міні-превʼю сторінки салону */}
                  <Box sx={{ bgcolor: c.header, px: 1, py: 0.6 }}>
                    <Typography sx={{ color: readableText(c.header), fontFamily: pff, fontWeight: 700, fontSize: 12, lineHeight: 1.1 }} noWrap>
                      {salonName}
                    </Typography>
                  </Box>
                  <Box sx={{
                    p: 1.25,
                    bgcolor: c.background,
                    ...(p.bgImageUrl ? { backgroundImage: `url(${mediaUrl(p.bgImageUrl)})`, backgroundSize: "cover", backgroundPosition: "center" } : {}),
                  }}>
                    <Box sx={{ bgcolor: c.surface, borderRadius: 1.5, p: 1, boxShadow: 1 }}>
                      <Typography sx={{ fontFamily: pff, color: c.text, fontWeight: 700, fontSize: 14, lineHeight: 1.15 }}>Aa</Typography>
                      <Box sx={{ height: 6, width: "60%", borderRadius: 3, bgcolor: c.accent, mt: 0.5, opacity: 0.8 }} />
                      <Box sx={{ mt: 0.75, bgcolor: c.primary, borderRadius: 1, py: 0.4, textAlign: "center" }}>
                        <Typography sx={{ color: readableText(c.primary), fontFamily: pff, fontSize: 10, fontWeight: 600 }}>
                          {t("book.book")}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  <Box sx={{ px: 1, py: 0.75, bgcolor: "background.paper" }}>
                    <Typography variant="body2" fontWeight={600} noWrap sx={{ lineHeight: 1.2 }}>{presetName(p, i18n.language)}</Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>{p.font}</Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Paper>

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 380px" }, gap: 3, alignItems: "start" }}>
          {/* ЛІВА КОЛОНКА — налаштування */}
          <Stack spacing={3}>
            <Paper sx={{ p: 2.5 }}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>
                {t("themeEditor.brandColors")}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: "block" }}>
                {t("themeEditor.brandColorsHint")}
              </Typography>
              <Stack spacing={2}>
                <ColorField disabled={!isOwner} t={t} label={t("themeEditor.brandColor")} hint={t("themeEditor.brandColorDesc")} val={primary} set={setPrimary} />
                <ColorField disabled={!isOwner} t={t} label={t("themeEditor.accentColor")} hint={t("themeEditor.accentColorDesc")} val={accent} set={setAccent} />
                <ColorField disabled={!isOwner} t={t} label={t("themeEditor.headerColor")} hint={t("themeEditor.headerColorDesc")} val={header} set={setHeader} />
              </Stack>
            </Paper>

            <Paper sx={{ p: 2.5 }}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>
                {t("themeEditor.pageColors")}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: "block" }}>
                {t("themeEditor.pageColorsHint")}
              </Typography>
              <Stack spacing={2}>
                <ColorField disabled={!isOwner} t={t} label={t("themeEditor.backgroundColor")} hint={t("themeEditor.backgroundColorDesc")} val={background} set={setBackground} />
                <ColorField disabled={!isOwner} t={t} label={t("themeEditor.surfaceColor")} hint={t("themeEditor.surfaceColorDesc")} val={surface} set={setSurface} />
                <ColorField disabled={!isOwner} t={t} label={t("themeEditor.textColor")} hint={t("themeEditor.textColorDesc")} val={text} set={setText} />
              </Stack>
              <Button size="small" onClick={resetColors} disabled={!isOwner} sx={{ mt: 2 }}>
                {t("themeEditor.resetColors")}
              </Button>
            </Paper>

            <Paper sx={{ p: 2.5 }}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
                {t("themeEditor.typography")}
              </Typography>
              <TextField
                select
                label={t("themeEditor.font")}
                value={font}
                onChange={(e) => setFont(e.target.value)}
                disabled={!isOwner}
                size="small"
                sx={{ width: 240 }}
              >
                {FONTS.map((f) => (
                  <MenuItem key={f} value={f} sx={{ fontFamily: `'${f}', sans-serif` }}>{f}</MenuItem>
                ))}
              </TextField>
            </Paper>

            <Paper sx={{ p: 2.5 }}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
                {t("themeEditor.logo")}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <TextField
                  label={t("themeEditor.logoUrl")}
                  value={logoUrl}
                  onChange={(e) => setLogoUrl(e.target.value)}
                  disabled={!isOwner}
                  size="small"
                  fullWidth
                  placeholder="https://…/logo.png"
                  helperText={t("themeEditor.logoHint")}
                />
                <Button component="label" size="small" variant="outlined"
                  disabled={!isOwner || uploadingLogo} sx={{ mt: 0.5, whiteSpace: "nowrap" }}>
                  {uploadingLogo ? t("themeEditor.uploading") : t("themeEditor.upload")}
                  <input hidden type="file" accept="image/*" onChange={onLogoPick} />
                </Button>
              </Stack>
              {logoUrl && (
                <Box component="img" src={mediaUrl(logoUrl)} alt="" sx={{ height: 40, mt: 1, objectFit: "contain" }}
                  onError={(e) => { e.currentTarget.style.display = "none"; }} />
              )}

              <Divider sx={{ my: 2.5 }} />
              <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                {t("themeEditor.bgImage")}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: "block" }}>
                {t("themeEditor.bgImageHint")}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <TextField
                  label={t("themeEditor.bgImageUrl")}
                  value={bgImageUrl}
                  onChange={(e) => setBgImageUrl(e.target.value)}
                  disabled={!isOwner}
                  size="small"
                  fullWidth
                  placeholder="https://…/background.jpg"
                />
                <Button component="label" size="small" variant="outlined"
                  disabled={!isOwner || uploadingBg} sx={{ mt: 0.5, whiteSpace: "nowrap" }}>
                  {uploadingBg ? t("themeEditor.uploading") : t("themeEditor.upload")}
                  <input hidden type="file" accept="image/*" onChange={onBgPick} />
                </Button>
                {bgImageUrl && (
                  <Button size="small" color="inherit" disabled={!isOwner} onClick={() => setBgImageUrl("")}
                    sx={{ mt: 0.5, whiteSpace: "nowrap" }}>
                    {t("themeEditor.clear")}
                  </Button>
                )}
              </Stack>
              {bgImageUrl && (
                <Box sx={{
                  mt: 1, height: 90, borderRadius: 1.5, border: "1px solid", borderColor: "divider",
                  backgroundImage: `url(${mediaUrl(bgImageUrl)})`, backgroundSize: "cover", backgroundPosition: "center",
                }} />
              )}
            </Paper>

            <Paper sx={{ p: 2.5 }}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
                {t("themeEditor.languages")}
              </Typography>
              <FormGroup row>
                {LANGS.map((l) => (
                  <FormControlLabel
                    key={l.code}
                    control={
                      <Checkbox checked={enabledLangs.includes(l.code)} onChange={() => toggleLang(l.code)} disabled={!isOwner} />
                    }
                    label={l.label}
                  />
                ))}
              </FormGroup>
              <TextField
                select
                label={t("themeEditor.defaultLang")}
                value={defaultLang}
                onChange={(e) => setDefaultLang(e.target.value)}
                disabled={!isOwner}
                sx={{ mt: 1, width: 220 }}
                size="small"
              >
                {LANGS.filter((l) => enabledLangs.includes(l.code)).map((l) => (
                  <MenuItem key={l.code} value={l.code}>{l.label}</MenuItem>
                ))}
              </TextField>
            </Paper>

            <Divider />
            <Box>
              <Button variant="contained" onClick={save} disabled={!isOwner || saving}>
                {saving ? t("themeEditor.saving") : t("themeEditor.save")}
              </Button>
            </Box>
          </Stack>

          {/* ПРАВА КОЛОНКА — живе прев'ю справжньої сторінки салону (sticky) */}
          <Box sx={{ position: { md: "sticky" }, top: 16 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              {t("themeEditor.preview")}
            </Typography>
            <Box sx={{
              borderRadius: 3, overflow: "hidden", border: "1px solid",
              borderColor: "divider", boxShadow: 3, fontFamily: ff,
            }}>
              {/* Брендована шапка */}
              <Box sx={{
                bgcolor: headBg, color: readableText(headBg), px: 2, py: 1.25,
                display: "flex", alignItems: "center", gap: 1,
              }}>
                {logoUrl && (
                  <Box component="img" src={mediaUrl(logoUrl)} alt=""
                    sx={{ height: 24, maxWidth: 90, objectFit: "contain" }}
                    onError={(e) => { e.currentTarget.style.display = "none"; }} />
                )}
                <Typography noWrap sx={{ fontWeight: 700, fontFamily: ff, flexGrow: 1, fontSize: 15 }}>
                  {salonName}
                </Typography>
                <LanguageIcon fontSize="small" />
              </Box>

              {/* Тіло сторінки */}
              <Box sx={{
                bgcolor: background, color: text, p: 2,
                ...(bgImageUrl ? {
                  backgroundImage: `url(${mediaUrl(bgImageUrl)})`,
                  backgroundSize: "cover", backgroundPosition: "center",
                } : {}),
              }}>
                <Typography sx={{ fontWeight: 700, fontFamily: ff, color: text, fontSize: 18, mb: 0.5 }}>
                  {t("themeEditor.previewTitle")}
                </Typography>
                <Typography sx={{ fontFamily: ff, color: text, opacity: 0.65, fontSize: 13, mb: 1.75 }}>
                  {t("themeEditor.previewText")}
                </Typography>

                {/* Картка послуги (surface) */}
                <Box sx={{
                  bgcolor: surface, borderRadius: 2, p: 1.5, mb: 1.75, boxShadow: 1,
                  border: "1px solid rgba(0,0,0,0.06)",
                  display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1,
                }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography noWrap sx={{ fontWeight: 600, fontFamily: ff, color: text, fontSize: 14 }}>
                      {t("themeEditor.previewService")}
                    </Typography>
                    <Typography sx={{ fontFamily: ff, color: text, opacity: 0.6, fontSize: 12 }}>
                      60 {t("common.min")}
                    </Typography>
                  </Box>
                  <Box sx={{
                    px: 1, py: 0.375, borderRadius: 1, flexShrink: 0,
                    bgcolor: accent, color: readableText(accent), fontFamily: ff,
                    fontSize: 12, fontWeight: 700,
                  }}>
                    120 zł
                  </Box>
                </Box>

                {/* Кнопка (primary) */}
                <Box sx={{
                  bgcolor: primary, color: readableText(primary), fontFamily: ff,
                  textAlign: "center", py: 1.125, borderRadius: 2, fontWeight: 700, fontSize: 14,
                }}>
                  {t("themeEditor.previewButton")}
                </Box>
              </Box>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
              {t("themeEditor.previewHint")}
            </Typography>
          </Box>
        </Box>
        </>
      )}
    </Box>
  );
}
